/**
 * Home Layout Context
 *
 * Provides centralized state management for the Home Layout Manager page.
 * Combines draft management, publishing, presence, and preview state.
 *
 * Real-time collaboration features:
 * - Socket.IO-based presence (replaces HTTP polling)
 * - Live publish notifications from other admins
 * - User joined/left events
 *
 * @module HomeLayoutManager/context
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import { httpClient } from '../../components/fetchApiData';
import config from '../../config';
import { useDraft, usePublish } from './hooks';
import { useSocketIO } from '../../providers/SocketIOProvider';
import Logger from '../../utils/Logger';
import { clearProductCache } from './api';
import type {
  HomeLayoutContextValue,
  DraftState,
  BannerConfig,
  CategoryConfig,
  BrandPositionsConfig,
  CarouselVerticalConfig,
  FeaturedBrandsConfig,
  ViewMode,
  ViewingSection,
  ActivePresence,
  Suggestion,
  ChangesResponse,
  PublishResponse,
  DragOperation,
} from './types';
import { ALL_LOCATIONS } from './types';

const API_BASE = `${config.API_URL}/admin/home-layout`;

/** Default context value (throws if used outside provider) */
const HomeLayoutContext = createContext<HomeLayoutContextValue | undefined>(undefined);

/**
 * Props for the HomeLayoutProvider component
 */
interface HomeLayoutProviderProps {
  /** React children to render */
  children: React.ReactNode;
}

/**
 * Provider component for the Home Layout Manager context
 *
 * Manages all state and actions for the unified home layout editing experience.
 * Provides draft management, publishing, presence awareness, and preview controls.
 *
 * @param props - Provider props
 * @returns Provider component wrapping children
 *
 * @example
 * ```tsx
 * <HomeLayoutProvider>
 *   <HomeLayoutManager />
 * </HomeLayoutProvider>
 * ```
 */
export function HomeLayoutProvider({ children }: HomeLayoutProviderProps): React.ReactElement {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  // A ref, so the socket handlers below don't re-subscribe when the identity loads
  const identityRef = useRef(identity);
  identityRef.current = identity;

  // Socket.IO integration for real-time collaboration
  const {
    isConnected: isSocketConnected,
    subscribeHomeLayout,
    unsubscribeHomeLayout,
    updatePresence: socketUpdatePresence,
    onPresenceUpdate,
    onHomeLayoutPublished,
    onUserJoined,
    onUserLeft,
  } = useSocketIO();

  // Fallback presence polling interval ref (used when Socket.IO is disconnected)
  const presenceIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce ref for suggestion fetches (avoids spamming on rapid draft updates)
  const suggestionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Draft management
  const {
    draft,
    liveState,
    isLoading: isDraftLoading,
    isSaving,
    error: draftError,
    lastModifiedBy,
    lastModifiedAt,
    hasChanges: draftHasChanges,
    fetchDraft,
    fetchLiveState,
    updateDraft: updateDraftHook,
    updateBannerConfig: updateBannerConfigHook,
    updateCategoryConfig: updateCategoryConfigHook,
    updateBrandPositions: updateBrandPositionsHook,
    resetDraft: resetDraftHook,
  } = useDraft({
    onDraftLoaded: () => {
      // Fetch live state for comparison when draft loads
      fetchLiveState();
      // Fetch changes
      fetchChanges();
      // Fetch suggestions
      fetchSuggestions();
    },
    onDraftUpdated: () => {
      // Refresh changes after draft update
      fetchChanges();
      debouncedFetchSuggestions();
    },
  });

  // Publish management
  const {
    isPublishing,
    publishHistory,
    publish: publishHook,
    fetchPublishHistory,
    rollback: rollbackHook,
  } = usePublish({
    onPublishSuccess: () => {
      // Refresh draft, live state, and changes after publish
      // Changes must be refreshed to reset hasChanges calculation
      fetchDraft();
      fetchLiveState();
      fetchChanges();
    },
    onRollbackSuccess: () => {
      // Refresh draft and changes after rollback
      fetchDraft();
      fetchChanges();
    },
  });

  // Preview state.
  //
  // Defaults to ALL LOCATIONS, not a single store. Content Control edits the one
  // global draft that every store renders from, so scoping it to a store made the
  // whole screen read "No products at this store" for anything that store doesn't
  // stock — hiding catalogue-wide content behind one outlet's shelf. A store is
  // still selectable from "Preview As" when you want to check a specific one.
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(ALL_LOCATIONS);
  const [viewMode, setViewMode] = useState<ViewMode>('mobile');
  const [compareMode, setCompareMode] = useState(false);

  // Presence state
  const [activeUsers, setActiveUsers] = useState<ActivePresence[]>([]);
  const [currentSection, setCurrentSection] = useState<ViewingSection>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  // Changes state
  const [changes, setChanges] = useState<ChangesResponse | null>(null);

  // Operations tracking (for meaningful change counting)
  const [pendingOperations, setPendingOperations] = useState<DragOperation[]>([]);

  // Publish notification from another admin
  const [publishNotification, setPublishNotification] = useState<{ publishedByName: string } | null>(null);
  const dismissPublishNotification = useCallback(() => setPublishNotification(null), []);

  // Combined loading state
  const isLoading = isDraftLoading;

  // Combined error state
  const error = draftError;

  // Combined hasChanges (from draft hook or changes calculation)
  const hasChanges = draftHasChanges || (changes?.totalChanges ?? 0) > 0 || pendingOperations.length > 0;

  /**
   * Track a drag-and-drop operation for meaningful change counting.
   * Upserts by itemId+section: keeps original fromPosition on re-drags,
   * removes the operation if the item returns to its original position.
   */
  const trackDragOperation = useCallback((op: DragOperation) => {
    setPendingOperations((prev) => {
      const existingIdx = prev.findIndex(
        (p) => p.itemId === op.itemId && p.section === op.section
      );
      if (existingIdx >= 0) {
        const existing = prev[existingIdx];
        // If moved back to original position, remove the operation
        if (existing.fromPosition === op.toPosition) {
          return prev.filter((_, i) => i !== existingIdx);
        }
        // Otherwise update toPosition but keep original fromPosition
        const updated = [...prev];
        updated[existingIdx] = {
          ...existing,
          toPosition: op.toPosition,
          lastMovedAt: op.lastMovedAt,
        };
        return updated;
      }
      // If from === to, no-op
      if (op.fromPosition === op.toPosition) return prev;
      return [...prev, op];
    });
  }, []);

  /**
   * Clear all pending operations (called on publish success and draft reset)
   */
  const clearOperations = useCallback(() => {
    setPendingOperations([]);
  }, []);

  /**
   * Meaningful change count: number of intentional drag operations
   * plus property-only changes from backend (excluding items already tracked as position changes)
   */
  const meaningfulChangeCount = useMemo(() => {
    const opCount = pendingOperations.length;
    if (!changes) return opCount;

    // Count property-only changes from backend that aren't already in our operations
    const operationItemIds = new Set(pendingOperations.map((op) => op.itemId));
    const propertyChanges = [
      ...(changes.categories || []),
      ...(changes.banners || []),
      ...(changes.brands || []),
    ].filter(
      (c) => !operationItemIds.has(c.id) && c.changeCategory !== 'position'
    );

    return opCount + propertyChanges.length;
  }, [pendingOperations, changes]);

  /**
   * Fetch changes between draft and live state
   */
  const fetchChanges = useCallback(async () => {
    try {
      const { json } = await httpClient(`${API_BASE}/changes`, { method: 'GET' });
      setChanges(json as ChangesResponse);
    } catch (err) {
      Logger.error('Failed to fetch changes:', err);
    }
  }, []);

  /**
   * Fetch optimization suggestions for current draft
   */
  const fetchSuggestions = useCallback(async () => {
    try {
      const { json } = await httpClient(`${API_BASE}/suggestions`, { method: 'GET' });
      setSuggestions((json as { suggestions: Suggestion[] }).suggestions);
    } catch (err) {
      Logger.error('Failed to fetch suggestions:', err);
    }
  }, []);

  /** Debounced version of fetchSuggestions — waits 2s after last call */
  const debouncedFetchSuggestions = useCallback(() => {
    if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
    suggestionsDebounceRef.current = setTimeout(() => {
      fetchSuggestions();
      suggestionsDebounceRef.current = null;
    }, 2000);
  }, [fetchSuggestions]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
    };
  }, []);

  /**
   * Fetch active users (presence) for collaboration awareness
   */
  const fetchPresence = useCallback(async () => {
    try {
      const { json } = await httpClient(`${API_BASE}/presence`, { method: 'GET' });
      setActiveUsers((json as { activeUsers: ActivePresence[] }).activeUsers);
    } catch (err) {
      Logger.error('Failed to fetch presence:', err);
    }
  }, []);

  /**
   * Update own presence state
   * Uses Socket.IO when connected, falls back to HTTP
   */
  const updatePresence = useCallback(async (
    section: ViewingSection,
    itemId: string | null = null
  ) => {
    setCurrentSection(section);
    setEditingItemId(itemId);

    // Use Socket.IO if connected
    if (isSocketConnected) {
      socketUpdatePresence(section, itemId);
      return;
    }

    // Fallback to HTTP
    try {
      await httpClient(`${API_BASE}/presence`, {
        method: 'POST',
        body: JSON.stringify({
          viewingSection: section,
          editingItemId: itemId,
        }),
      });
      // Refresh presence after update
      fetchPresence();
    } catch (err) {
      Logger.error('Failed to update presence:', err);
    }
  }, [isSocketConnected, socketUpdatePresence, fetchPresence]);

  // Wrapped update functions that also update presence
  const updateDraft = useCallback(async (updates: Partial<DraftState>) => {
    await updateDraftHook(updates);
  }, [updateDraftHook]);

  const updateBannerConfig = useCallback(async (config: Partial<BannerConfig>) => {
    await updateBannerConfigHook(config);
  }, [updateBannerConfigHook]);

  const updateCategoryConfig = useCallback(async (config: CategoryConfig) => {
    await updateCategoryConfigHook(config);
  }, [updateCategoryConfigHook]);

  const updateBrandPositions = useCallback(async (config: BrandPositionsConfig) => {
    await updateBrandPositionsHook(config);
  }, [updateBrandPositionsHook]);

  const updateCarouselVerticalConfig = useCallback(async (config: CarouselVerticalConfig) => {
    await updateDraftHook({ carouselVerticalConfig: config });
  }, [updateDraftHook]);

  const updateFeaturedBrandsConfig = useCallback(async (config: FeaturedBrandsConfig) => {
    await updateDraftHook({ featuredBrandsConfig: config });
  }, [updateDraftHook]);

  const resetDraft = useCallback(async () => {
    await resetDraftHook();
    clearOperations();
    // Clear stale changes immediately so hasChanges flips to false without waiting for API
    setChanges(null);
    // Then refresh live state, changes, and suggestions from backend
    fetchLiveState();
    fetchChanges();
    fetchSuggestions();
  }, [resetDraftHook, clearOperations, fetchLiveState, fetchChanges, fetchSuggestions]);

  const publish = useCallback(async (notes?: string): Promise<PublishResponse> => {
    const result = await publishHook(notes, pendingOperations);
    if (!result) {
      throw new Error('Publish failed');
    }
    clearOperations();
    setChanges(null); // Clear stale changes immediately
    clearProductCache(); // Invalidate cached product counts after publish
    return result;
  }, [publishHook, pendingOperations, clearOperations]);

  const rollback = useCallback(async (publishId: string) => {
    await rollbackHook(publishId);
  }, [rollbackHook]);

  const refreshDraft = useCallback(async () => {
    await fetchDraft();
    await fetchLiveState();
    fetchChanges();
    fetchSuggestions();
  }, [fetchDraft, fetchLiveState, fetchChanges, fetchSuggestions]);

  // Initial data fetch - runs once on mount
  // Use refs to avoid stale closures while keeping effect stable
  const fetchDraftRef = useRef(fetchDraft);
  const fetchPublishHistoryRef = useRef(fetchPublishHistory);
  const fetchPresenceRef = useRef(fetchPresence);

  useEffect(() => {
    fetchDraftRef.current = fetchDraft;
    fetchPublishHistoryRef.current = fetchPublishHistory;
    fetchPresenceRef.current = fetchPresence;
  });

  useEffect(() => {
    fetchDraftRef.current();
    fetchPublishHistoryRef.current();
    fetchPresenceRef.current();
  }, []); // Empty array - run once on mount

  // Use refs for presence polling to avoid re-creating interval
  const currentSectionRef = useRef(currentSection);
  const editingItemIdRef = useRef(editingItemId);
  const updatePresenceRef = useRef(updatePresence);

  useEffect(() => {
    currentSectionRef.current = currentSection;
    editingItemIdRef.current = editingItemId;
    updatePresenceRef.current = updatePresence;
  });

  // Socket.IO subscription and event handlers
  useEffect(() => {
    if (!isSocketConnected) return;

    // Subscribe to home-layout room
    subscribeHomeLayout();

    // Register event handlers
    const unsubPresence = onPresenceUpdate((presence) => {
      // Transform Socket.IO presence format to our ActivePresence format
      const transformed: ActivePresence[] = presence.map((p) => ({
        user: {
          id: p.userId,
          name: p.userName,
          email: '', // Not sent in Socket.IO presence
          avatarUrl: p.avatarUrl || null,
        },
        viewingSection: p.viewingSection as ViewingSection,
        editingItemId: p.editingItemId,
        lastPingAt: p.timestamp,
      }));
      setActiveUsers(transformed);
    });

    const unsubPublished = onHomeLayoutPublished((event) => {
      // Another admin published changes - show blocking dialog and refresh
      setPublishNotification({ publishedByName: event.publishedBy.name });
      // Refresh draft and changes to show latest state
      fetchDraftRef.current();
      fetchChanges();
    });

    const unsubUserJoined = onUserJoined((user) => {
      // Skip our own joins (another tab, or a reconnect): the server only leaves out the joining socket
      const me = identityRef.current;
      // userId is the DB user id the socket sends; me.id is the Cognito sub, so email is the fallback
      const isMe = !!me && ((!!me.userId && user.id === String(me.userId)) || (!!user.email && user.email.toLowerCase() === String(me.fullName ?? '').toLowerCase()));
      if (isMe) return;
      notify(`${user.name} joined Content Control`, { type: 'info' });
    });

    const unsubUserLeft = onUserLeft(() => {
      // Optionally notify when user leaves (commented out to reduce noise)
      // notify(`User left Content Control`, { type: 'info' });
    });

    // Cleanup on unmount
    return () => {
      unsubPresence();
      unsubPublished();
      unsubUserJoined();
      unsubUserLeft();
      unsubscribeHomeLayout();
    };
  }, [isSocketConnected, subscribeHomeLayout, unsubscribeHomeLayout, onPresenceUpdate, onHomeLayoutPublished, onUserJoined, onUserLeft, notify, fetchChanges]);

  // Fallback presence polling (every 30 seconds) when Socket.IO is not connected
  useEffect(() => {
    // Only poll if Socket.IO is not connected
    if (isSocketConnected) {
      // Clear any existing polling interval when Socket.IO connects
      if (presenceIntervalRef.current) {
        clearInterval(presenceIntervalRef.current);
        presenceIntervalRef.current = null;
      }
      return;
    }

    presenceIntervalRef.current = setInterval(() => {
      // Re-send presence to keep alive
      if (currentSectionRef.current) {
        updatePresenceRef.current(currentSectionRef.current, editingItemIdRef.current);
      }
      // Refresh presence list
      fetchPresenceRef.current();
    }, 30000);

    return () => {
      if (presenceIntervalRef.current) {
        clearInterval(presenceIntervalRef.current);
      }
    };
  }, [isSocketConnected]); // Re-run when Socket.IO connection status changes

  // Memoized context value
  const contextValue = useMemo<HomeLayoutContextValue>(() => ({
    // State
    draft,
    liveState,
    isLoading,
    isSaving,
    isPublishing,
    error,
    lastModifiedBy,
    lastModifiedAt,
    hasChanges,
    changes,

    // Preview state
    selectedStoreId,
    viewMode,
    compareMode,

    // Presence state
    activeUsers,

    // Suggestions
    suggestions,

    // Operations tracking
    pendingOperations,
    meaningfulChangeCount,

    // Actions
    updateDraft,
    updateBannerConfig,
    updateCategoryConfig,
    updateBrandPositions,
    updateCarouselVerticalConfig,
    updateFeaturedBrandsConfig,
    resetDraft,
    publish,
    rollback,
    refreshDraft,

    // Preview actions
    setSelectedStoreId,
    setViewMode,
    setCompareMode,

    // Operations actions
    trackDragOperation,
    clearOperations,

    // Presence actions
    updatePresence,

    // Publish notification
    publishNotification,
    dismissPublishNotification,
  }), [
    draft,
    liveState,
    isLoading,
    isSaving,
    isPublishing,
    error,
    lastModifiedBy,
    lastModifiedAt,
    hasChanges,
    changes,
    selectedStoreId,
    viewMode,
    compareMode,
    activeUsers,
    suggestions,
    pendingOperations,
    meaningfulChangeCount,
    updateDraft,
    updateBannerConfig,
    updateCategoryConfig,
    updateBrandPositions,
    updateCarouselVerticalConfig,
    updateFeaturedBrandsConfig,
    resetDraft,
    publish,
    rollback,
    refreshDraft,
    trackDragOperation,
    clearOperations,
    updatePresence,
    publishNotification,
    dismissPublishNotification,
  ]);

  return (
    <HomeLayoutContext.Provider value={contextValue}>
      {children}
    </HomeLayoutContext.Provider>
  );
}

/**
 * Hook to access the Home Layout context
 *
 * Must be used within a HomeLayoutProvider.
 *
 * @returns The Home Layout context value
 * @throws Error if used outside of HomeLayoutProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { draft, updateDraft, publish } = useHomeLayout();
 *   // ...
 * }
 * ```
 */
export function useHomeLayout(): HomeLayoutContextValue {
  const context = useContext(HomeLayoutContext);
  if (context === undefined) {
    throw new Error('useHomeLayout must be used within a HomeLayoutProvider');
  }
  return context;
}

export default HomeLayoutContext;
